import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Link as LinkIcon, Copy, Check, EyeOff, Share2 } from "lucide-react";

export default function ShareModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState(null);
  const [working, setWorking] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = token ? `${window.location.origin}/share/${token}` : "";

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/share/status");
        setEnabled(!!data.enabled);
        setToken(data.token);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, [open]);

  const enable = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/share/create");
      setToken(data.token);
      setEnabled(true);
      toast.success("Share link ready — copy and send");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setWorking(false); }
  };

  const revoke = async () => {
    setWorking(true);
    try {
      await api.post("/share/revoke");
      setEnabled(false);
      setToken(null);
      toast.success("Previous link revoked");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setWorking(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#FDFCFA]" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A362D] flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#D99C3D]" />
            Share your pipeline
          </DialogTitle>
          <DialogDescription>
            Send a read-only snapshot of your applications to a mentor, coach, or accountability partner.
            Anyone with the link can view — no sign-in needed. Revoke any time.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-[#5C6B64] py-6">Loading…</p>
        ) : enabled && token ? (
          <div className="space-y-4" data-testid="share-active">
            <div className="rounded-xl bg-[#E2F0E7] border border-[#1A362D]/10 p-4 flex items-start gap-3">
              <LinkIcon className="w-4 h-4 text-[#1A362D] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[#1A362D] font-medium text-sm">Your snapshot is live</p>
                <p className="text-[#5C6B64] text-xs mt-0.5">Only people with this exact link can see it.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={shareUrl}
                className="bg-white font-mono text-xs"
                onFocus={(e) => e.target.select()}
                data-testid="share-url-input"
              />
              <Button onClick={copy} variant="outline" className="rounded-full shrink-0" data-testid="share-copy-btn">
                {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy</>}
              </Button>
            </div>
            <div className="pt-4 border-t border-[#E5E1DB] flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-[#1A362D] font-medium">Changed your mind?</p>
                <p className="text-xs text-[#5C6B64] mt-0.5">Revoking makes the current link stop working immediately.</p>
              </div>
              <Button
                onClick={revoke}
                disabled={working}
                variant="outline"
                className="rounded-full border-[#C65D47]/40 text-[#C65D47] hover:bg-[#FCECEC]"
                data-testid="share-revoke-btn"
              >
                <EyeOff className="w-4 h-4 mr-1.5" />
                {working ? "Revoking…" : "Revoke link"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="share-inactive">
            <ul className="space-y-2 text-sm text-[#5C6B64]">
              <li>• A branded, read-only view of your applications and stats</li>
              <li>• Nobody can edit, add, or see your account</li>
              <li>• Revoke any time with one click</li>
            </ul>
            <Button
              onClick={enable}
              disabled={working}
              className="w-full h-11 rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white"
              data-testid="share-enable-btn"
            >
              {working ? "Creating…" : "Create share link"}
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="share-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
